import { type QueryKey, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { m } from "@recobra/i18n/messages";
import type { DocumentType, GenerateUploadUrlRequest } from "@recobra/schemas/document";

import { documentsApi, DuplicateDocumentUploadError, uploadToS3 } from "../lib/api";
import { getFileFingerprint } from "./FileFingerprint";
import {
  errorMessageFrom,
  getInitialUploadPhase,
  resolveMimeType,
  toDisplayDoc,
} from "./UploadHelpers";
import type { UploadFileEntry, UploadPhase, UploadRow } from "./UploadTypes";

const COMPLETED_UPLOAD_DISMISS_DELAY_MS = 1500;

type RelatedEntityType = GenerateUploadUrlRequest["relatedEntityType"];

export const uploadDocumentsForEntity = async ({
  files,
  relatedEntityId,
  relatedEntityType,
}: {
  files: File[];
  relatedEntityId: string;
  relatedEntityType: RelatedEntityType;
}): Promise<boolean> => {
  if (files.length === 0) return true;

  toast.info(m.doc_uploading_count({ count: files.length }));

  const uploadPromises = files.map(async (file) => {
    try {
      const fileFingerprint = await getFileFingerprint(file);
      const mimeType = resolveMimeType(file);
      const { uploadUrl, contentType } = await documentsApi.requestUploadUrl({
        relatedEntityId,
        relatedEntityType,
        filename: file.name,
        mimeType,
        fileFingerprint,
        sizeBytes: file.size,
      });

      await uploadToS3(uploadUrl, file, contentType, () => {});
    } catch (err) {
      console.error(`Failed to upload ${file.name}`, err);
      toast.error(m.doc_upload_failed_file({ name: file.name }));

      throw err;
    }
  });

  try {
    await Promise.all(uploadPromises);
    toast.success(m.doc_upload_success_all());

    return true;
  } catch {
    toast.error(m.doc_upload_failed_some());

    return false;
  }
};

export const useDocumentUploads = ({
  relatedEntityId,
  relatedEntityType,
  documentsQueryKey,
  getExistingFingerprints,
  documentType,
}: {
  relatedEntityId: string;
  relatedEntityType: RelatedEntityType;
  documentsQueryKey: QueryKey;
  getExistingFingerprints?: () => Iterable<string | undefined>;
  documentType?: DocumentType;
}) => {
  const queryClient = useQueryClient();
  const [uploads, setUploads] = useState<UploadFileEntry[]>([]);
  const uploadsRef = useRef(uploads);
  uploadsRef.current = uploads;

  const setUploadPhase = useCallback((id: string, phase: UploadPhase) => {
    setUploads((prev) => prev.map((upload) => (upload.id === id ? { ...upload, phase } : upload)));
  }, []);

  const runUpload = useCallback(
    async (upload: UploadFileEntry) => {
      const { id: uploadId, file, fileFingerprint } = upload;
      const setPhase = (phase: UploadPhase) => setUploadPhase(uploadId, phase);

      setPhase({ status: "requesting-url" });

      try {
        const mimeType = resolveMimeType(file);
        const { documentId, uploadUrl, contentType } = await documentsApi.requestUploadUrl({
          relatedEntityId,
          relatedEntityType,
          filename: file.name,
          mimeType,
          fileFingerprint,
          sizeBytes: file.size,
          ...(documentType ? { documentType } : {}),
        });

        setPhase({ status: "uploading", progress: 0 });
        await uploadToS3(uploadUrl, file, contentType, (progress) =>
          setPhase({ status: "uploading", progress }),
        );

        setPhase({ status: "complete", documentId });
        void queryClient.invalidateQueries({ queryKey: documentsQueryKey });

        setTimeout(() => {
          setUploads((prev) => prev.filter((entry) => entry.id !== uploadId));
        }, COMPLETED_UPLOAD_DISMISS_DELAY_MS);
      } catch (err) {
        if (err instanceof DuplicateDocumentUploadError) {
          toast.error(m.doc_upload_duplicate_file({ name: file.name }));
        } else {
          toast.error(m.doc_upload_failed_file({ name: file.name }));
        }

        setPhase({ status: "error", message: errorMessageFrom(err) });
      }
    },
    [
      documentsQueryKey,
      documentType,
      queryClient,
      relatedEntityId,
      relatedEntityType,
      setUploadPhase,
    ],
  );

  const addFiles = useCallback(
    async (files: File[]) => {
      const seenFingerprints = new Set(
        [
          ...(getExistingFingerprints?.() ?? []),
          ...uploadsRef.current.map((upload) => upload.fileFingerprint),
        ].filter((value): value is string => value !== undefined),
      );
      const newUploads: UploadFileEntry[] = [];

      for (const file of files) {
        const fileFingerprint = await getFileFingerprint(file);
        if (seenFingerprints.has(fileFingerprint)) {
          toast.error(m.doc_upload_duplicate_file({ name: file.name }));
          continue;
        }

        seenFingerprints.add(fileFingerprint);
        newUploads.push({
          id: crypto.randomUUID(),
          file,
          fileFingerprint,
          phase: getInitialUploadPhase(file),
        });
      }

      if (newUploads.length === 0) return;

      setUploads((prev) => [...prev, ...newUploads]);
      newUploads
        .filter((upload) => upload.phase.status === "idle")
        .forEach((upload) => void runUpload(upload));
    },
    [getExistingFingerprints, runUpload],
  );

  const removeUpload = useCallback((id: string) => {
    setUploads((prev) => {
      const upload = prev.find((entry) => entry.id === id);
      if (upload && upload.phase.status !== "idle" && upload.phase.status !== "error") {
        return prev;
      }

      return prev.filter((entry) => entry.id !== id);
    });
  }, []);

  const retryUpload = useCallback(
    (id: string) => {
      const upload = uploads.find((entry) => entry.id === id);
      if (!upload || upload.phase.status !== "error" || upload.phase.retryable === false) return;

      void runUpload(upload);
    },
    [runUpload, uploads],
  );

  const rows = useMemo<UploadRow[]>(
    () =>
      uploads.map((upload) => ({
        kind: "upload",
        id: upload.id,
        entry: upload,
        doc: toDisplayDoc(upload),
      })),
    [uploads],
  );
  const allDone =
    uploads.length > 0 && uploads.every((upload) => upload.phase.status === "complete");

  return {
    addFiles,
    allDone,
    removeUpload,
    retryUpload,
    rows,
    uploads,
  };
};
